'use client';

import { type FC, useMemo, type RefObject } from 'react';
import { OverlayBadge } from '../../components/ui';
import { ItemListItem } from '../../components/ItemListItem';
import { InfiniteItemGrid } from '../../components/InfiniteItemGrid';
import { ItemPrice, useSteamPriceAllowed } from '../../components/ItemPrice';
import { useInventoryInfinite } from '../../queries/useInventoryQueries';
import { useLocalTranslation } from '../../hooks/useLocalTranslation.ts';
import { cn } from '../../utils/cn';
import type { UserInventoryItemResponse } from '../../services/api.ts';
import translations from './ContractScreen.translations.ts';

interface ContractInventoryListProps {
  selectedIds: Set<string>;
  maxItems: number;
  onToggle: (item: UserInventoryItemResponse) => void;
  scrollRef: RefObject<HTMLDivElement | null>;
}

const SelectedCheck: FC = () => (
  <span className="w-6 h-6 rounded-full bg-accent-blue text-white flex items-center justify-center shadow-[0_2px_6px_rgba(0,0,0,0.5)]">
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M3 7l3 3 5-6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

export const ContractInventoryList: FC<ContractInventoryListProps> = ({ selectedIds, maxItems, onToggle, scrollRef }) => {
  const { t } = useLocalTranslation(translations);
  const steamPriceAllowed = useSteamPriceAllowed();
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
    refetch,
  } = useInventoryInfinite('available');

  const items = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

  const renderItem = (item: UserInventoryItemResponse) => {
    const selected = selectedIds.has(item.id);
    const atCap = !selected && selectedIds.size >= maxItems;

    return (
      <ItemListItem
        key={item.id}
        item={{
          name: item.item.name,
          imageUrl: item.item.imageUrl ?? '',
          rarityColor: item.item.rarityColor,
        }}
        onClick={atCap ? undefined : () => onToggle(item)}
        className={cn('transition-all duration-200', {
          'ring-[3px] ring-inset ring-accent-blue': selected,
          'opacity-30 cursor-not-allowed': atCap,
        })}
        topRightContent={selected ? <SelectedCheck /> : undefined}
      >
        <OverlayBadge>
          <ItemPrice credits={item.acquiredPriceInCredits} variant="cardPrice" />
        </OverlayBadge>
        {steamPriceAllowed && item.item.steamPrice != null && (
          <OverlayBadge>
            <ItemPrice steamPrice={item.item.steamPrice} steamIconSize={11} variant="steamPrice" />
          </OverlayBadge>
        )}
      </ItemListItem>
    );
  };

  return (
    <InfiniteItemGrid
      items={items}
      isLoading={isLoading}
      isError={isError}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      fetchNextPage={() => fetchNextPage()}
      refetch={() => refetch()}
      scrollRef={scrollRef}
      renderItem={renderItem}
      emptyTitle={t('selectionEmptyInventory')}
      errorText={t('errorsLoadInventoryFailed')}
    />
  );
};
